import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output, ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {CcBaseComponent} from '../../../cc-base.component';
import {FormRichFieldConfig} from '../../../../model/api/form-rich-field-config';
import {FormFieldConfig} from '../../../../model/api/form-field-config';
import {CcSubFormBaseComponent} from "../cc-sub-form-base.component";
import {CcFormDialogComponent} from "../../cc-form-dialog.component";

@Component({
  selector: 'cc-rich-field[fieldName][formRichFieldConfig][formRecords]',
  templateUrl: './rich-field.component.html',
  styleUrls: ['./rich-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    TranslateService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RichFieldComponent extends CcSubFormBaseComponent {

  @Input()
  formRichFieldConfig: FormRichFieldConfig;

  contextMenuConfig: MenuItem[];

  @Input()
  fieldName: String;

  @Input()
  formRecords: any[];
  @Output()
  formRecordsChange = new EventEmitter<any[]>();

  @Input()
  required: boolean;

  @Input()
  disabled: boolean;

  @Input()
  transPartKey: string;

  fieldValueMap = {};

  dialogFormVisible: boolean;
  dialogFormTitle: string = '';

  selectedRecord: any;
  selectedRecords: any[];

  submitted: boolean;
  tableLoading: boolean;

  formRecord: any;
  editedFormRecordCopy: any;

  @ViewChild('ccFormDialog')
  ccFormDialog: CcFormDialogComponent;

  constructor(cdref: ChangeDetectorRef,
              messageService: MessageService,
              translateService: TranslateService,
              private confirmationService: ConfirmationService) {
    super(cdref, messageService, translateService);
  }

  // tslint:disable-next-line:use-life-cycle-interface
  ngOnInit() {
    // protected initConfigs() {
    //   super.initConfigs();
    this.contextMenuConfig = [
      {
        label: this.getTranslation('common.table.edit_button'), icon: 'fas fa-pen', command: () => {
          this.edit(this.selectedRecord);
        }
      },
      {
        label: this.getTranslation('common.table.clone_button'), icon: 'fas fa-clone', command: () => {
          this.clone(this.selectedRecord);
        }
      },
      {
        separator: true
      },
      {
        label: this.getTranslation('common.table.delete_button'), icon: 'fas fa-trash-alt', command: () => {
          // // @ts-ignore
          // if (!this.selectedRecords.find(value => value[this.fieldId] === this.formRecord[this.fieldId])) {
          //   this.selectedRecords = [this.formRecord];
          // }
          // this.selectedRecords = [this.formRecord];
          this.delete([this.selectedRecord]);
        }
      }
    ];
  }

  openNew() {
    this.dialogFormTitle = this.getTranslation('common.table.create_item') + ' '
      + this.fieldName + 's';
    this.editedFormRecordCopy = undefined;
    this.formRecord = {};
    this.submitted = false;
    this.dialogFormVisible = true;
    this.markForCheck();
  }

  clone(record: any) {
    this.dialogFormTitle = this.getTranslation('common.table.clone_item') + ' '
      + this.fieldName + 's';
    this.editedFormRecordCopy = undefined;
    this.formRecord = JSON.parse(JSON.stringify(record));
    if (this.formRichFieldConfig.fieldId !== this.formRichFieldConfig.fieldName
      && this.formRecord[this.formRichFieldConfig.fieldId]) {
      this.formRecord[this.formRichFieldConfig.fieldId] = undefined;
    }
    if (this.formRecord[this.formRichFieldConfig.fieldName]) {
      this.formRecord[this.formRichFieldConfig.fieldName] = this.formRecord[this.formRichFieldConfig.fieldName] + '_clone';
    }
    this.submitted = false;
    this.dialogFormVisible = true;
    this.markForCheck();
  }

  delete(records: any[]) {
    this.tableLoading = true;
    this.confirmationService.confirm({
      header: this.getTranslation('common.table.delete_item')
        + ' ' + this.getTranslation('page.' + this.transPartKey + '.item_name') + (records.length === 1 ? '' : 's'),
      message: records.length > 1 ? (this.getTranslation('common.dialog.confirm_delete_plu') + ' ' + this.fieldName + 's?')
        : (this.getTranslation('common.dialog.confirm_delete_sing') + ' ' + this.fieldName + '?'),
      icon: 'fas fa-exclamation-triangle',
      acceptLabel: this.getTranslation('common.dialog.delete'),
      acceptButtonStyleClass: 'p-button-danger p-button-sm',
      rejectLabel: this.getTranslation('common.dialog.cancel'),
      rejectButtonStyleClass: 'p-button-warning p-button-sm',
      accept: () => {
        this.formRecords = this.formRecords.filter(val => !records.includes(val));
        this.selectedRecords = undefined;
        this.tableLoading = false;
        this.reloadFieldValueMap();
        this.formRecordsChange.emit(this.formRecords);
      },
      reject: () => {
        this.tableLoading = false;
      }
    });
  }

  edit(record: any) {
    this.dialogFormTitle = this.getTranslation('common.table.edit_item') + ' '
      + this.fieldName + 's';
    this.editedFormRecordCopy = record;
    this.formRecord = JSON.parse(JSON.stringify(record)); // Object.assign({}, record);
    this.dialogFormVisible = true;
    this.markForCheck();
  }

  hideDialog() {
    this.dialogFormVisible = false;
    this.submitted = false;
    this.markForCheck();
  }

  save() {
    this.submitted = true;

    if (this.editedFormRecordCopy) {
      const index = this.formRecords.indexOf(this.editedFormRecordCopy);
      this.formRecords[index] = this.formRecord;

      this.editedFormRecordCopy = undefined;
      this.successMessage(this.fieldName + ' Updated');
    } else {
      this.formRecords.push(this.formRecord);
    }
    this.formRecord = undefined;
    this.dialogFormVisible = false;

    this.reloadFieldValueMap();
    this.formRecordsChange.emit(this.formRecords);
    this.markForCheck();
  }

  onRowRightClick_(record: any) {
  }

  private validateForm(): string {
    if (!this.formRecord) {
      return undefined;
    }
    let message;
    this.formRichFieldConfig.fields.forEach(configGrp => {
      configGrp.fields.forEach(config => {
        if (!message) {
          const fieldMessage = this.validateField(config);
          if (fieldMessage) {
            message = fieldMessage;
          }
        }
      });
    });
    return message;
  }

  private validateField(fieldConfig: FormFieldConfig): string {
    if (!this.formRecord) {
      return '';
    }
    const formRecord = this.formRecord;
    let message;
    if (this.formRecords.length > 0) {
      if (fieldConfig.required
        && (!this.formRecord[fieldConfig.field] || (this.formRecord[fieldConfig.field] + '').length === 0)) {
        message = this.getTranslation('page.' + this.transPartKey + '.form.' + fieldConfig.field) + ' '
          + this.getTranslation('common.form.required_sing');
      } else if (fieldConfig.validate) {
        message = fieldConfig.validate(this.formRecord);
      } else if (fieldConfig.field === this.formRichFieldConfig.fieldId
        && (!this.editedFormRecordCopy || this.editedFormRecordCopy[fieldConfig.field] !== formRecord[fieldConfig.field])
        && this.formRecords && this.formRecords.filter(function (record) {
          return record[fieldConfig.field] === formRecord[fieldConfig.field];
        }).length > 0) {
        message = this.getTranslation('page.' + this.transPartKey + '.form.' + fieldConfig.field) + ' '
          + this.getTranslation('common.form.must_be_unique');
      }
    }
    return message;
  }

  reloadFieldValueMap() {
    this.fieldValueMap = {};
    if (this.formRichFieldConfig && this.formRichFieldConfig.tableConfigs && this.formRecords)
      this.formRichFieldConfig.tableConfigs.forEach((conf, index) => {
        if (conf.format && conf.format.includes('#'))
          conf.format = '1.0-7';
        this.formRecords.forEach((record, indexRec) => {
          if (conf.mapId) {
            const value = conf.mapId(record);
            if (value.includes('<'))
              this.fieldValueMap['mapId-' + index + '_' + indexRec] = value;
            this.fieldValueMap['mapId-inner-' + index + '_' + indexRec] = value;
          }
        });
      });
    this.fieldValueMap['validate'] = this.validateForm();
    super.reloadFieldValueMap();
    if (this.ccFormDialog)
      this.ccFormDialog.reloadFieldValueMap();
  }
}

